/**
 * LocationPickerComponent
 * Map-based location picker using Leaflet
 *
 * Library: leaflet (open-source, no API keys required)
 * Note: Production deployment would need leaflet CSS and marker assets
 */

import React, { useCallback, useEffect, useRef, useMemo, memo } from 'react';
import type { FormControlProps } from '../../types';

export interface LocationValue {
  lat: number;
  lng: number;
  address?: string;
  zoom?: number;
}

interface LocationPickerComponentProps extends FormControlProps {
  mapProvider?: 'leaflet' | 'google-maps';
  defaultLat?: number;
  defaultLng?: number;
  defaultZoom?: number;
  maxZoom?: number;
  minZoom?: number;
  showSearch?: boolean;
  showGeolocation?: boolean;
  onChange?: (location: LocationValue) => void;
}

export const LocationPickerComponent: React.FC<LocationPickerComponentProps> = memo(({
  value = { lat: 0, lng: 0 },
  mapProvider = 'leaflet',
  defaultLat = 0,
  defaultLng = 0,
  defaultZoom = 12,
  maxZoom = 18,
  minZoom = 2,
  showSearch = true,
  showGeolocation = true,
  onChange,
  disabled = false,
  required = false,
  label,
  error,
  helpText,
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const inputId = useMemo(
    () => `location-picker-${label?.replace(/\s+/g, '-').toLowerCase()}`,
    [label],
  );

  const locationValue = useMemo(() => {
    if (typeof value === 'object' && value !== null && 'lat' in value) {
      return value as LocationValue;
    }
    return { lat: defaultLat, lng: defaultLng, zoom: defaultZoom };
  }, [value, defaultLat, defaultLng, defaultZoom]);

  const handleGeolocation = useCallback(() => {
    if (!navigator.geolocation) {
      console.error('Geolocation not supported');
      return;
    }

    navigator.geolocation.getCurrentPosition((position) => {
      const location: LocationValue = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        zoom: defaultZoom,
      };
      onChange?.(location);
    });
  }, [defaultZoom, onChange]);

  const handleLocationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, field: 'lat' | 'lng') => {
      const newValue = Number(e.currentTarget.value);
      onChange?.({
        ...locationValue,
        [field]: newValue,
      });
    },
    [locationValue, onChange],
  );

  return (
    <div className="af-form-field__group location-picker-group">
      {label && (
        <label className="af-form-field__label">
          {label}
          {required && <span className="af-form-field__required">*</span>}
        </label>
      )}

      <div className="location-picker__container">
        {showSearch && (
          <div className="location-picker__search">
            <input
              type="text"
              placeholder="Search location..."
              disabled={disabled}
              className="location-picker__search-input"
              aria-label="Search location"
            />
          </div>
        )}

        <div
          ref={mapContainerRef}
          className="location-picker__map"
          id={`${inputId}-map`}
          style={{ height: '400px', backgroundColor: '#e0e0e0' }}
          role="region"
          aria-label="Map region"
        >
          [Map would render here with Leaflet/Google Maps library]
        </div>

        <div className="location-picker__coordinates">
          <div className="location-picker__input-group">
            <label htmlFor={`${inputId}-lat`} className="location-picker__label">
              Latitude
            </label>
            <input
              id={`${inputId}-lat`}
              type="number"
              value={locationValue.lat}
              onChange={(e) => handleLocationChange(e, 'lat')}
              min="-90"
              max="90"
              step="0.0001"
              disabled={disabled}
              className="location-picker__input"
              aria-label="Latitude"
            />
          </div>

          <div className="location-picker__input-group">
            <label htmlFor={`${inputId}-lng`} className="location-picker__label">
              Longitude
            </label>
            <input
              id={`${inputId}-lng`}
              type="number"
              value={locationValue.lng}
              onChange={(e) => handleLocationChange(e, 'lng')}
              min="-180"
              max="180"
              step="0.0001"
              disabled={disabled}
              className="location-picker__input"
              aria-label="Longitude"
            />
          </div>
        </div>

        {showGeolocation && (
          <button
            type="button"
            onClick={handleGeolocation}
            disabled={disabled}
            className="location-picker__geolocation-button"
            aria-label="Use my current location"
          >
            Use Current Location
          </button>
        )}
      </div>

      {error && (
        <div id={`${inputId}-error`} className="af-form-field__error">
          {error}
        </div>
      )}

      {helpText && (
        <div id={`${inputId}-help`} className="af-form-field__help">
          {helpText}
        </div>
      )}
    </div>
  );
});
